import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router';
import styled from 'styled-components';
import { PATHS } from 'shared/consts/paths';

// ─── Styled components ────────────────────────────────────────────────────────

const Container = styled.main`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    padding: 80px 20px 100px;
    @media (max-width: 415px) {
        padding: 56px 15px 72px;
    }
`;

const Inner = styled.div`
    width: 100%;
    max-width: 620px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
`;

const ErrorCode = styled.span`
    font-size: 96px;
    font-weight: 700;
    color: #1268fb;
    line-height: 1;
    letter-spacing: -2px;
    @media (max-width: 415px) {
        font-size: 72px;
    }
`;

const Heading = styled.h1`
    font-size: 26px;
    font-weight: 600;
    color: #111827;
    margin: 16px 0 12px;
`;

const Subtext = styled.p`
    font-size: 16px;
    line-height: 26px;
    color: #6b7280;
    margin: 0 0 40px;
    max-width: 480px;
`;

const UploadCta = styled(Link)`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #1268fb;
    color: #fff;
    font-size: 16px;
    font-weight: 600;
    padding: 14px 32px;
    border-radius: 10px;
    text-decoration: none;
    transition: background 0.15s;
    &:hover {
        background: #0f5ad6;
    }
`;

const Divider = styled.hr`
    width: 100%;
    border: none;
    border-top: 1px solid #e5e7eb;
    margin: 48px 0 40px;
`;

const SectionLabel = styled.h2`
    font-size: 15px;
    font-weight: 600;
    color: #9ca3af;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin: 0 0 20px;
`;

const LinkGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    width: 100%;
    @media (max-width: 500px) {
        grid-template-columns: 1fr;
    }
`;

const LinkCard = styled(Link)`
    display: flex;
    flex-direction: column;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    padding: 16px;
    text-decoration: none;
    text-align: left;
    transition: border-color 0.15s, background 0.15s;
    &:hover {
        border-color: #1268fb44;
        background: #1268fb06;
    }
`;

const LinkCardTitle = styled.span`
    font-size: 15px;
    font-weight: 600;
    color: #111827;
    margin-bottom: 4px;
`;

const LinkCardDesc = styled.span`
    font-size: 13px;
    line-height: 19px;
    color: #6b7280;
`;

// ─── Page ─────────────────────────────────────────────────────────────────────

const ErrorPage: React.FC = () => {
    return (
        <>
            <Helmet>
                <title>404 — Page not found | PhoneCheckr</title>
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>
            <Container>
                <Inner>
                    <ErrorCode>404</ErrorCode>
                    <Heading>This page doesn't exist</Heading>
                    <Subtext>
                        The URL may have changed or the page may have been removed. You can validate your phone list or
                        browse our guides below.
                    </Subtext>
                    <UploadCta to={PATHS.HOME}>Validate your phone list</UploadCta>

                    <Divider />

                    <SectionLabel>Popular guides</SectionLabel>
                    <LinkGrid>
                        <LinkCard to={PATHS.FREE_PHONE_LOOKUP}>
                            <LinkCardTitle>Free phone number lookup</LinkCardTitle>
                            <LinkCardDesc>Check any number for line type, carrier, and validity.</LinkCardDesc>
                        </LinkCard>
                        <LinkCard to={PATHS.VOIP_CHECKER}>
                            <LinkCardTitle>VOIP checker</LinkCardTitle>
                            <LinkCardDesc>Find out if a number is VOIP, mobile, or landline.</LinkCardDesc>
                        </LinkCard>
                        <LinkCard to={PATHS.CARRIER_LOOKUP}>
                            <LinkCardTitle>Carrier lookup</LinkCardTitle>
                            <LinkCardDesc>Identify the carrier for any US or Canadian number.</LinkCardDesc>
                        </LinkCard>
                        <LinkCard to={PATHS.BULK_PHONE_VALIDATION}>
                            <LinkCardTitle>Bulk phone validation</LinkCardTitle>
                            <LinkCardDesc>Upload a CSV and validate your entire phone list at once.</LinkCardDesc>
                        </LinkCard>
                        <LinkCard to={PATHS.HOW_TO_VERIFY_PHONE}>
                            <LinkCardTitle>How to verify a phone number</LinkCardTitle>
                            <LinkCardDesc>Step-by-step guide to verifying any phone number.</LinkCardDesc>
                        </LinkCard>
                        <LinkCard to={PATHS.CHECK_MOBILE_OR_LANDLINE}>
                            <LinkCardTitle>Check mobile or landline</LinkCardTitle>
                            <LinkCardDesc>Determine if a number is a cell phone or wired line.</LinkCardDesc>
                        </LinkCard>
                    </LinkGrid>
                </Inner>
            </Container>
        </>
    );
};

export default ErrorPage;
